<template>
  <v-app
    class="
      helpdesk-layout
    "
  >
    <!-- ====================================================== -->
    <!-- UTILITY BAR -->
    <!-- ====================================================== -->

    <v-system-bar
      height="28"
      flat
      class="
        helpdesk-utility-bar
      "
    >
      <div
        class="
          helpdesk-utility-inner
        "
      >
        <div
          class="
            d-flex
            align-center
          "
        >
          <button
            type="button"
            class="
              helpdesk-utility-link
            "
            @click="goToContact"
          >
            {{
              lang === "fr"
                ? "Contactez-nous"
                : "Contact us"
            }}
          </button>

          <span
            class="
              helpdesk-account-name
            "
          >
            <v-icon
              size="14"
              class="mr-1"
            >
              mdi-account
            </v-icon>

            {{
              displayName
            }}
          </span>
        </div>

        <div
          class="
            d-flex
            align-center
            ga-1
          "
        >
          <v-btn
            icon
            variant="text"
            size="x-small"
            @click="
              searchDialogOpen =
                true
            "
          >
            <v-icon size="17">
              mdi-magnify
            </v-icon>
          </v-btn>

          <button
            type="button"
            class="
              helpdesk-currency
            "
          >
            {{
              selectedCurrency
            }}
          </button>
        </div>
      </div>
    </v-system-bar>


    <SearchDialogheader
      v-model="
        searchDialogOpen
      "
    />


    <!-- ====================================================== -->
    <!-- APP BAR -->
    <!-- ====================================================== -->

    <v-app-bar
      flat
      height="64"
      class="
        helpdesk-app-bar
      "
      :style="{
        background:
          theme.background,
        color:
          theme.color
      }"
    >
      <!-- SIDEBAR -->

      <div
        class="
          d-flex
          align-center
        "
      >
        <v-btn
          icon
          size="small"
          variant="text"
          @click="
            helpdeskUi
              .toggleSidebar()
          "
        >
          <v-icon>
            mdi-menu
          </v-icon>
        </v-btn>

        <v-btn
          v-if="mdAndUp"
          icon
          size="small"
          variant="text"
          @click="
            helpdeskUi
              .togglePin()
          "
        >
          <v-icon
            :color="
              helpdeskUi
                .isPinned
                ? 'primary'
                : undefined
            "
          >
            mdi-pin
          </v-icon>
        </v-btn>
      </div>


      <!-- LOGO -->

      <router-link
        :to="{
          name:
            'PublicHome',
          params: {
            lang
          }
        }"
        class="
          helpdesk-logo-link
        "
      >
        <v-img
          :src="
            currentLogo
          "
          contain
          class="
            helpdesk-logo
          "
        />
      </router-link>


      <v-divider
        vertical
        class="mx-2"
      />


      <!-- DESKTOP MENUS -->

      <div
        v-if="mdAndUp"
        class="
          d-flex
          align-center
        "
      >
        <MegaMenuSetup />

        <MegaMenu />

        <MegaMenuResources />
      </div>


      <v-spacer />


      <!-- THEME -->

      <v-menu
        v-model="
          themeMenu
        "
        location="
          bottom end
        "
      >
        <template
          #activator="{
            props
          }"
        >
          <v-btn
            v-bind="props"
            icon
            size="small"
            variant="text"
          >
            <v-icon>
              mdi-theme-light-dark
            </v-icon>
          </v-btn>
        </template>

        <v-list
          density="compact"
        >
          <v-list-item
            prepend-icon="
              mdi-white-balance-sunny
            "
            title="Light"
            @click="
              setTheme(
                'light'
              )
            "
          />

          <v-list-item
            prepend-icon="
              mdi-weather-night
            "
            title="Dark"
            @click="
              setTheme(
                'dark'
              )
            "
          />

          <v-list-item
            prepend-icon="
              mdi-theme-light-dark
            "
            title="Hybrid"
            @click="
              setTheme(
                'hybrid'
              )
            "
          />
        </v-list>
      </v-menu>


      <LanguageSwitcher
        @change="
          onLanguageChange
        "
      />
    </v-app-bar>


    <!-- ====================================================== -->
    <!-- MAIN SHELL -->
    <!-- ====================================================== -->

    <v-navigation-drawer
      v-model="
        helpdeskUi.sidebarOpen
      "
      :permanent="
        helpdeskUi.isPinned &&
        mdAndUp
      "
      :temporary="
        !mdAndUp
      "
      :width="
        drawerWidth
      "
      class="
        helpdesk-drawer
      "
    >
      <HelpdeskSidebar
        @resize="
          handleSidebarResize
        "
        @panel-state="
          handleSidebarResize
        "
      />
    </v-navigation-drawer>


    <v-main
      class="
        helpdesk-main
      "
    >
      <!--
        IMPORTANT:
        ONLY ONE ROUTER VIEW EXISTS
        IN THIS COMPONENT.
      -->

      <router-view />

      <HelpdeskFooter />
    </v-main>
  </v-app>
</template>


<script setup>
import {
  computed,
  nextTick,
  onMounted,
  ref,
  watch
} from "vue"

import {
  useRouter
} from "vue-router"

import {
  useDisplay
} from "vuetify"

import {
  useI18n
} from "vue-i18n"

import HelpdeskSidebar
  from "@/modules/helpdesk/components/HelpdeskSidebar.vue"

import HelpdeskFooter
  from "@/modules/helpdesk/components/HelpdeskFooter.vue"

import MegaMenu
  from "@/components/MegaMenu.vue"

import MegaMenuSetup
  from "@/components/MegaMenuSetup.vue"

import MegaMenuResources
  from "@/components/MegaMenuResources.vue"

import LanguageSwitcher
  from "@/components/LanguageSwitcher.vue"

import SearchDialogheader
  from "@/components/SearchDialogheader.vue"

import api
  from "@/auth/api"

import {
  useAuthStore,
  useThemeStore
} from "@/store/auth"

import {
  useLangStore
} from "@/store/lang"

import {
  usehelpdeskUiStore
} from "@/modules/helpdesk/store/helpdeskUi.store"


const router =
  useRouter()

const {
  mdAndUp
} =
  useDisplay()

const {
  locale
} =
  useI18n()


const auth =
  useAuthStore()

const themeStore =
  useThemeStore()

const langStore =
  useLangStore()

const helpdeskUi =
  usehelpdeskUiStore()


// ============================================================
// LANGUAGE
// ============================================================

const lang =
  computed(
    () =>
      langStore
        .currentLanguage ||
      "en"
  )

watch(
  lang,
  value => {
    locale.value =
      value
  },
  {
    immediate: true
  }
)


function onLanguageChange(
  value
) {
  locale.value =
    value

  nextTick(() => {})
}


// ============================================================
// THEME
// ============================================================

const theme =
  computed(() => ({
    background:
      themeStore
        .themeStyles
        ?.background ||
      "#ffffff",

    color:
      themeStore
        .themeStyles
        ?.color ||
      "#333333"
  }))


const themeMenu =
  ref(false)


function setTheme(
  value
) {
  themeStore.setTheme(
    value
  )

  localStorage.setItem(
    "currentTheme",
    value
  )

  themeMenu.value =
    false
}


// ============================================================
// USER
// ============================================================

const displayName =
  computed(
    () =>
      auth.loginName ||
      auth.user?.name ||
      auth.user
        ?.displayName ||
      "Customer"
  )


// ============================================================
// SEARCH
// ============================================================

const searchDialogOpen =
  ref(false)


// ============================================================
// CURRENCY
// ============================================================

const selectedCurrency =
  ref(
    localStorage.getItem(
      "currency"
    ) ||
    "CAD"
  )


// ============================================================
// LOGOS
// ============================================================

const logoLight =
  ref("")

const logoDark =
  ref("")

const currentTheme =
  computed(
    () =>
      localStorage.getItem(
        "currentTheme"
      ) ||
      "light"
  )


const currentLogo =
  computed(() =>
    currentTheme.value ===
    "light"
      ? logoLight.value
      : logoDark.value
  )


// ============================================================
// SIDEBAR WIDTH
// ============================================================

const RAIL_WIDTH =
  64

const drawerWidth =
  ref(
    344
  )


function handleSidebarResize(
  detail
) {
  if (!mdAndUp.value) {
    return
  }

  const width =
    Number(
      detail?.totalWidth
    )

  if (
    !Number.isFinite(
      width
    ) ||
    width <
      RAIL_WIDTH
  ) {
    return
  }

  drawerWidth.value =
    width
}


// ============================================================
// NAVIGATION
// ============================================================

function goToContact() {
  console.log(
    "[HelpdeskLayout] Contact"
  )
}


// ============================================================
// INIT
// ============================================================

onMounted(
  async () => {
    logoLight.value =
      localStorage.getItem(
        "LogoLight"
      ) ||
      ""

    logoDark.value =
      localStorage.getItem(
        "LogoDark"
      ) ||
      ""

    try {
      const response =
        await api.get(
          "/Tenants/current"
        )

      if (
        response.data
          ?.subscriptionStatus ===
        0
      ) {
        await router.push({
          name:
            lang.value ===
            "fr"
              ? "BillingFR"
              : "BillingEN",

          params: {
            lang:
              lang.value
          }
        })
      }
    }
    catch (error) {
      console.warn(
        "[HelpdeskLayout] Tenant load failed.",
        error
      )
    }
  }
)
</script>


<style scoped>
.helpdesk-layout {
  height: 100dvh;
  overflow: hidden;
}

.helpdesk-utility-bar {
  width: 100% !important;
  background:
    #f0f1ff !important;
  color:
    #333333 !important;
}

.helpdesk-utility-inner {
  display: flex;
  width: 100%;
  height: 28px;
  align-items: center;
  justify-content:
    space-between;
  padding: 0 30px;
}

.helpdesk-utility-link,
.helpdesk-currency {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 11px;
}

.helpdesk-account-name {
  margin-left: 12px;
  font-size: 11px;
}

.helpdesk-app-bar {
  padding-left:
    30px !important;
  padding-right:
    30px !important;
}

.helpdesk-logo-link {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  text-decoration: none;
}

.helpdesk-logo {
  width: 180px;
  height: 42px;
}

.helpdesk-drawer {
  overflow:
    visible !important;
}

.helpdesk-main {
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

@media (
  max-width: 959px
) {
  .helpdesk-app-bar {
    padding-left:
      8px !important;
    padding-right:
      8px !important;
  }

  .helpdesk-logo {
    width: 135px;
  }
}
</style>